import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowLeft, Mail, ShoppingBag, ShieldCheck, ChevronDown, Trees } from "lucide-react";
import { api } from "@/lib/api";
import Nav from "@/components/sections/Nav";
import Footer from "@/components/sections/Footer";
import ProductCarousel from "@/components/sections/ProductCarousel";

const CAT_LABEL = {
  fertilizer: "Fertilizer",
  weed_and_feed: "Weed & Feed",
  seed: "Seed",
  apparel: "Apparel",
};

const SECTIONS = [
  { key: "apparel", label: "Apparel", anchor: "apparel" },
  { key: "outdoor", label: "Outdoor Products", anchor: "outdoor" },
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false); // dropdown
  const ddRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products")
      .then((r) => setProducts(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    function onClick(e) {
      if (ddRef.current && !ddRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const apparel = useMemo(
    () => products.filter((p) => p.category === "apparel"),
    [products]
  );
  const outdoor = useMemo(
    () => products.filter((p) => p.category !== "apparel"),
    [products]
  );

  const jumpTo = (anchor) => {
    setOpen(false);
    const el = document.getElementById(anchor);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="shop-page" className="bg-background text-foreground min-h-screen">
      <Nav />

      <main className="pt-32 md:pt-40">
        {/* Top band */}
        <section className="container-ic pb-12 md:pb-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary mb-8"
            data-testid="back-home"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back home
          </Link>
          <div className="overline mb-4">
            <ShoppingBag className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
            Ironclad Shop
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.9]">
            Products that
            <br />
            <span className="text-primary">do the work.</span>
          </h1>
          <p className="mt-6 text-muted-foreground text-lg max-w-2xl">
            The same gear, fertilizer, weed control, and seed we trust on every job.
            Browse — call or email to order. Local delivery across Greater San Antonio.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            {/* Dropdown */}
            <div className="relative" ref={ddRef}>
              <button
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center justify-between gap-3 min-w-[240px] border border-primary/40 bg-card/30 px-5 py-3 text-xs uppercase tracking-widest hover:border-primary transition-colors"
                data-testid="shop-dropdown-toggle"
              >
                Browse Sections
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
                />
              </button>
              {open && (
                <div
                  className="absolute left-0 mt-1 min-w-[240px] border border-border bg-black z-30 shadow-xl"
                  data-testid="shop-dropdown-menu"
                >
                  {SECTIONS.map((s) => (
                    <button
                      key={s.key}
                      onClick={() => jumpTo(s.anchor)}
                      className="w-full text-left px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary hover:bg-accent transition-colors border-b border-border last:border-b-0"
                      data-testid={`shop-dropdown-${s.key}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a href="tel:5129480676" className="btn-primary" data-testid="shop-call">
              <Phone className="h-4 w-4" /> Call to Order
            </a>
            <a
              href="mailto:ironcladatx@gmail.com?subject=Ironclad%20Shop%20Order"
              className="btn-outline"
              data-testid="shop-email"
            >
              <Mail className="h-4 w-4" /> Email an Order
            </a>
          </div>
        </section>

        {loading ? (
          <div className="container-ic py-20 text-center text-muted-foreground">
            Loading…
          </div>
        ) : (
          <>
            {/* APPAREL SECTION */}
            <CategorySection
              id="apparel"
              overline="01 · Apparel"
              title={
                <>
                  Built to <span className="text-primary">outwork</span> the Texas sun.
                </>
              }
              intro="Sporty, outdoorsman gear in jet black and Ironclad green. Embroidered on the chest, engineered for the season."
              icon={ShieldCheck}
              products={apparel}
              accent="apparel"
            />

            {/* OUTDOOR PRODUCTS SECTION */}
            <CategorySection
              id="outdoor"
              overline="02 · Outdoor Products"
              title={
                <>
                  Fertilizer, weed & feed, <span className="text-primary">seed.</span>
                </>
              }
              intro="The exact products we put down on San Antonio-area lawns. Engineered for Bermuda, St. Augustine, and 100°F Texas heat."
              icon={Trees}
              products={outdoor}
              accent="outdoor"
            />
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

function CategorySection({ id, overline, title, intro, icon: Icon, products, accent }) {
  return (
    <section
      id={id}
      data-testid={`section-${id}`}
      className="py-20 md:py-24 border-t border-border bg-background"
    >
      <div className="container-ic">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-10">
          <div className="lg:col-span-2">
            <div className="w-14 h-14 border border-primary/40 flex items-center justify-center">
              <Icon className="h-6 w-6 text-primary" />
            </div>
          </div>
          <div className="lg:col-span-10">
            <div className="overline mb-3">{overline}</div>
            <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
              {title}
            </h2>
            <p className="mt-5 text-muted-foreground text-lg max-w-3xl">{intro}</p>
          </div>
        </div>
      </div>

      {/* Carousel */}
      {products.length > 0 && (
        <div className="container-ic">
          <ProductCarousel
            items={products}
            height={280}
            speed={accent === "apparel" ? 42 : 50}
            testId={`carousel-${id}`}
          />
        </div>
      )}

      {/* Grid */}
      <div className="container-ic mt-10">
        {products.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            No products in this section yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <article
                key={p.id}
                className="border border-border bg-card/30 flex flex-col group"
                data-testid={`product-${p.id}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-accent/40">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 bg-black/80 text-primary text-[10px] uppercase tracking-widest px-2 py-1 border border-primary/40">
                    {CAT_LABEL[p.category] || p.category}
                  </span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display text-lg md:text-xl font-bold uppercase tracking-tight leading-tight">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">
                    {p.description}
                  </p>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="font-display text-2xl font-black text-primary">
                      ${p.price.toFixed(2)}
                    </div>
                    <a
                      href={`mailto:ironcladatx@gmail.com?subject=Order%20-%20${encodeURIComponent(
                        p.name
                      )}`}
                      className="text-xs uppercase tracking-widest border border-border px-3 py-2 hover:border-primary hover:text-primary transition-colors"
                      data-testid={`order-${p.id}`}
                    >
                      Order
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
